import React from 'react';

import cx from 'classnames';
import { pipe } from 'ramda';
import { useParams, useRouteMatch } from 'react-router-dom';

import getNavLinks, { getItineraryNavLinks } from 'components/Pages/Admin/Page/Landing/utils';
import LandingPagesContainer from 'components/shared/LandingPage/components/Grid';
import Loader from 'components/shared/Loader';
import { useAsyncAction } from 'hooks/useAsyncAction';
import { fetchSampleItinerariesItemThunk, fetchSampleLookBookItemThunk } from 'store/itinerariesItem/thunks';
import { getPage } from 'store/page/thunks';
import { getLandingTheme, isItineraries, isLookbook } from 'utils/itineraries/functions';

import { getLandingForViewMode } from '../../../PropertyPortal/functions';
import { EDetailsMode } from '../../Itinerary/Details/types';
import { getIsSamplePreviewDefault, getPageInfo, getPreviewFetchArgs } from './functions';
import styles from './index.module.css';
import LandingPreview from './Landing';
import landingStyles from './Landing/index.module.css';
import LandingV2Preview from './LandingV2';
import SamplePreview from './SamplePreview';

function ModalWrapper({ children, className }) {
  return <div className={cx(styles.modal, className)}>{children}</div>;
}

function LoadingWrapper({ children, modalWrapperClassName }) {
  return (
    <ModalWrapper className={modalWrapperClassName}>
      <div className={styles.loading}>{children}</div>
    </ModalWrapper>
  );
}

const anchorSectionNameMap = {
  Rooms: 'Accommodations',
};

function Preview(props) {
  const {
    itinerary,
    pageId,
    initialAnchorSection,
    fetchData,
    getIsSamplePreview = getIsSamplePreviewDefault,
    modalWrapperClassName,
    deviceType = undefined,
  } = props;
  const { content } = useParams();
  const { path } = useRouteMatch();
  const isSamplePreview = getIsSamplePreview({ content, path });

  const fetchPreviewData = (() => {
    if (isSamplePreview && isItineraries(content)) {
      return fetchSampleItinerariesItemThunk;
    } else if (isSamplePreview && isLookbook(content)) {
      return fetchSampleLookBookItemThunk;
    } else if (!isSamplePreview && getPage) {
      return getPage;
    }

    return fetchData;
  })();
  const {
    data: rawData,
    loading,
    error,
  } = useAsyncAction(fetchPreviewData, {
    args: getPreviewFetchArgs({ itinerary, pageId }),
  });

  // TODO: Uncomment when SSR Lookbooks and Itineraries will go public
  // if ((itinerary?.type === 'lookbook' || itinerary?.type === 'itinerary'))  {
  //   const updateZoom = (increment) => () => {
  //     const iframe = document.querySelector(`#preview-${itinerary.id}`);
  //     iframe.style.setProperty(
  //       '--zoom',
  //       `${parseFloat(getComputedStyle(iframe).getPropertyValue('--zoom')) + increment}`,
  //     );
  //   };
  //
  //   return (
  //     <ModalWrapper className={styles.iframeWrapper}>
  //       <iframe id={`preview-${itinerary.id}`} src={itinerary.dashboardPreviewUrl} className={styles.iframe} />
  //       <div className={styles.iframeZoomControls}>
  //         <IconButton
  //           onClick={updateZoom(0.1)}
  //           icon={Plus}
  //           iconProps={{ width: '16px', height: '16px' }}
  //           className={styles.iframeZoomButton}
  //         />
  //         <IconButton
  //           onClick={updateZoom(-0.1)}
  //           icon={Minus}
  //           iconProps={{ width: '16px', height: '16px' }}
  //           className={styles.iframeZoomButton}
  //         />
  //       </div>
  //     </ModalWrapper>
  //   );
  // }

  if (loading) {
    return (
      <LoadingWrapper modalWrapperClassName={modalWrapperClassName}>
        <Loader />
      </LoadingWrapper>
    );
  }

  if (error) {
    return (
      <LoadingWrapper modalWrapperClassName={modalWrapperClassName}>
        <div className={styles.error}>Oops! Something went wrong</div>
      </LoadingWrapper>
    );
  }

  if (rawData && !loading) {
    const data = getLandingForViewMode(rawData);

    if (data.detailsMode === EDetailsMode.Chronological) {
      return (
        <ModalWrapper className={modalWrapperClassName}>
          <LandingV2Preview data={data} deviceType={deviceType} />
        </ModalWrapper>
      );
    }

    const landingData = data?.landing;
    const navLinks = getNavLinks(landingData);
    const anchorSection = initialAnchorSection
      ? navLinks.find((link) => link.includes(anchorSectionNameMap[initialAnchorSection] ?? initialAnchorSection))
      : undefined;

    if (isSamplePreview) {
      const itineraryNavLinks = getItineraryNavLinks(landingData.pages);
      const itineraryLandingTheme = pipe(getPageInfo, getLandingTheme)(data);

      return (
        <ModalWrapper className={modalWrapperClassName}>
          <LandingPagesContainer
            className={cx(
              landingStyles.landingPagesContainer,
              `custom-main-${itineraryLandingTheme}`,
              'custom-main-preview',
            )}
          >
            {landingData.pages.map((page, i) => {
              if (!page.headline) {
                page.headline = data.title;
              }

              return (
                <SamplePreview
                  key={i}
                  landingData={page}
                  page={data}
                  anchor={itineraryNavLinks[i]}
                  links={itineraryNavLinks}
                  pageIndex={i}
                  landingTheme={itineraryLandingTheme}
                />
              );
            })}
          </LandingPagesContainer>
        </ModalWrapper>
      );
    }

    const landingPage = landingData.pages[0];

    if (!landingPage.title) {
      landingPage.title = data.name;
    }

    return (
      <ModalWrapper className={modalWrapperClassName}>
        <LandingPreview
          initialAnchorSection={anchorSection}
          landingData={landingPage}
          page={data}
          links={navLinks}
          isPreview
        />
      </ModalWrapper>
    );
  }

  return (
    <LoadingWrapper modalWrapperClassName={modalWrapperClassName}>
      <div className={styles.error}>Oops! Something went wrong</div>
    </LoadingWrapper>
  );
}

export default Preview;
